import type { FormationDetailProps, FormationProps } from '@/types/formation';
import { siteConfig } from '@/config/site.config';

import { sstFormation } from './sst-formation';
import { macSstFormation } from './mac-sst-formation';
import { initiationPremierSecoursFormation } from './initiation-premiers-secours-formation';
import { epiFormation } from './epi-formation';
import { esiFormation } from './esi-formation';
import { espaceConfineFormation } from './espace-confine-formation';
import { gestesPosturesFormation } from './gestes-postures-formation';
import { ariFormation } from './ari-formation';
import { preCatecFormation } from './pre-catec-formation';
import { catecFormation } from './catec-formation';
import { macCatecFormation } from './mac-catec-formation';
import { hydrocurageFormation } from './hydrocurage-formation';
import { hydrocurageImmoFormation } from './hydrocurage-immo-formation';
import { extincteurFormation } from './extincteurs-formation';
import { detecteurGazFormation } from './detecteur-gaz-formation';
import { verificateurEpiFormation } from './verificateur-epi-formation';
import { habilitationElectriqueFormation } from './habilitation-electrique-bs-be-formation';
import { habilitationElectriqueB0H0Formation } from './habilitation-electrique-b0-h0-formation';
import { portHarnaisAntichutesFormation } from './port-harnais-antichute-formation';
import { balisageChantierFormation } from './balisage-chantier-formation';
import { atmosphereExplosiveFormation } from './atmosphere-explosive-formation';
import { travailHauteurFormation } from './travail-hauteur-formation';
import { daeFormation } from './dae-formation';
import { cseFormation } from './cse-formation';
import { aiprFormation } from './aipr-formation';
import { hpThpFormation } from './hp-thp-formation';

/**
 * Source unique de toutes les formations détaillées.
 * Chaque formation est indexée par son slug pour un accès rapide.
 */
const defaultFormatterInfo = {
  name: siteConfig.name,
  phone: siteConfig.contact.phoneDisplay,
  email: siteConfig.contact.email,
  website: new URL(siteConfig.url).host,
};

function normalizeFormatterInfo(formation: FormationDetailProps): FormationDetailProps {
  if (!formation.formatterInfo) {
    return formation;
  }

  return {
    ...formation,
    formatterInfo: {
      name: formation.formatterInfo.name || defaultFormatterInfo.name,
      phone: defaultFormatterInfo.phone,
      email: defaultFormatterInfo.email,
      website: defaultFormatterInfo.website,
    },
  };
}

const rawFormationsDetail: Record<string, FormationDetailProps> = {
  'sauveteur-secouriste-travail': sstFormation,
  'mac-sst': macSstFormation,
  'initiation-premiers-secours': initiationPremierSecoursFormation,
  'equipier-premiere-intervention': epiFormation,
  'equipier-seconde-intervention': esiFormation,
  'espace-confine': espaceConfineFormation,
  'gestes-et-postures': gestesPosturesFormation,
  'appareil-respiratoire-isolant': ariFormation,
  'pre-catec': preCatecFormation,
  'catec': catecFormation,
  'mac-catec': macCatecFormation,
  'hydrocurage': hydrocurageFormation,
  'hydrocurage-immo': hydrocurageImmoFormation,
  'manipulation-extincteurs': extincteurFormation,
  'detecteur-gaz': detecteurGazFormation,
  'verificateur-epi': verificateurEpiFormation,
  'habilitation-electrique-bs-be-manoeuvre': habilitationElectriqueFormation,
  'habilitation-electrique-b0-h0': habilitationElectriqueB0H0Formation,
  'port-harnais-systemes-antichute': portHarnaisAntichutesFormation,
  'balisage-chantier': balisageChantierFormation,
  'atmosphere-explosive-niveau-0': atmosphereExplosiveFormation,
  'travail-hauteur': travailHauteurFormation,
  'dae': daeFormation,
  'cse': cseFormation,
  'aipr': aiprFormation,
  'hp-thp': hpThpFormation,
};

export const formationsDetail: Record<string, FormationDetailProps> = Object.fromEntries(
  Object.entries(rawFormationsDetail).map(([slug, formation]) => [slug, normalizeFormatterInfo(formation)]),
) as Record<string, FormationDetailProps>;

// Catalogue principal
export const formationsList: FormationProps[] = Object.values(formationsDetail).map((formation) => ({
  id: formation.id,
  title: formation.title,
  description: formation.description,
  duration: formation.duration,
  category: formation.category,
  imageSrc: formation.imageSrc,
  slug: formation.slug,
  reference: formation.reference,
  price: formation.price,
  habilitation: formation.habilitation,
}));

/**
 * Liste de tous les slugs disponibles (pour generateStaticParams).
 */
export const allFormationSlugs = Object.keys(formationsDetail);

/**
 * Récupère une formation par son slug.
 */
export function getFormationBySlug(slug: string): FormationDetailProps | undefined {
  return formationsDetail[slug];
}
